import type { SolanaTransactionInput } from '@txviz/core';
import { hexToBytes } from '@txviz/core';
import { base58Decode } from './types.js';

/**
 * Serialize a Solana transaction message into its binary format.
 *
 * This produces the exact bytes that Ed25519 signs over.
 * Solana does NOT hash the message before signing — Ed25519 signs
 * the raw message bytes directly.
 *
 * If serializedMessage is provided, we use it directly.
 * Otherwise, we reconstruct the legacy message from fields.
 */
export function serializeSolanaTransaction(tx: SolanaTransactionInput): Uint8Array {
  if (tx.serializedMessage) {
    // User provided pre-serialized message
    if (tx.serializedMessage.startsWith('0x')) {
      return hexToBytes(tx.serializedMessage);
    }
    // Assume base58 if not hex
    return base58Decode(tx.serializedMessage);
  }

  return serializeLegacyMessage(tx);
}

/**
 * Serialize a legacy Solana transaction message.
 *
 * Format:
 *   header (3 bytes) | accountKeys[] | recentBlockhash | instructions[]
 */
function serializeLegacyMessage(tx: SolanaTransactionInput): Uint8Array {
  // Collect all unique accounts in the correct order:
  // 1. Writable signers
  // 2. Read-only signers
  // 3. Writable non-signers
  // 4. Read-only non-signers
  const accountMap = new Map<string, { isSigner: boolean; isWritable: boolean }>();

  // Fee payer is always first, always signer + writable
  accountMap.set(tx.feePayer, { isSigner: true, isWritable: true });

  // Add all accounts from instructions
  for (const ix of tx.instructions) {
    for (const acc of ix.accounts) {
      const existing = accountMap.get(acc.pubkey);
      if (existing) {
        accountMap.set(acc.pubkey, {
          isSigner: existing.isSigner || acc.isSigner,
          isWritable: existing.isWritable || acc.isWritable,
        });
      } else {
        accountMap.set(acc.pubkey, {
          isSigner: acc.isSigner,
          isWritable: acc.isWritable,
        });
      }
    }

    // Program IDs are read-only non-signers
    if (!accountMap.has(ix.programId)) {
      accountMap.set(ix.programId, { isSigner: false, isWritable: false });
    }
  }

  // Sort into the four groups
  const writableSigners: string[] = [];
  const readonlySigners: string[] = [];
  const writableNonSigners: string[] = [];
  const readonlyNonSigners: string[] = [];

  for (const [pubkey, meta] of accountMap) {
    if (meta.isSigner && meta.isWritable) writableSigners.push(pubkey);
    else if (meta.isSigner && !meta.isWritable) readonlySigners.push(pubkey);
    else if (!meta.isSigner && meta.isWritable) writableNonSigners.push(pubkey);
    else readonlyNonSigners.push(pubkey);
  }

  // Fee payer must be first in writableSigners
  const feePayerIdx = writableSigners.indexOf(tx.feePayer);
  if (feePayerIdx > 0) {
    writableSigners.splice(feePayerIdx, 1);
    writableSigners.unshift(tx.feePayer);
  }

  const orderedAccounts = [
    ...writableSigners,
    ...readonlySigners,
    ...writableNonSigners,
    ...readonlyNonSigners,
  ];

  const accountIndex = new Map<string, number>();
  orderedAccounts.forEach((pubkey, idx) => accountIndex.set(pubkey, idx));

  // Header
  const numRequiredSignatures = writableSigners.length + readonlySigners.length;
  const numReadonlySignedAccounts = readonlySigners.length;
  const numReadonlyUnsignedAccounts = readonlyNonSigners.length;

  const parts: Uint8Array[] = [];

  // Header (3 bytes)
  parts.push(new Uint8Array([
    numRequiredSignatures,
    numReadonlySignedAccounts,
    numReadonlyUnsignedAccounts,
  ]));

  // Account keys count (compact-u16) + keys (each 32 bytes)
  parts.push(encodeCompactU16(orderedAccounts.length));
  for (const pubkey of orderedAccounts) {
    parts.push(base58Decode(pubkey));
  }

  // Recent blockhash (32 bytes)
  parts.push(base58Decode(tx.recentBlockhash));

  // Instructions count (compact-u16) + compiled instructions
  parts.push(encodeCompactU16(tx.instructions.length));
  for (const ix of tx.instructions) {
    const programIdIndex = accountIndex.get(ix.programId);
    if (programIdIndex === undefined) {
      throw new Error(`Program ID ${ix.programId} not found in account keys`);
    }

    // Program ID index (1 byte)
    parts.push(new Uint8Array([programIdIndex]));

    // Account indexes
    parts.push(encodeCompactU16(ix.accounts.length));
    const accountIndexes = new Uint8Array(ix.accounts.length);
    for (let i = 0; i < ix.accounts.length; i++) {
      const acc = ix.accounts[i]!;
      const idx = accountIndex.get(acc.pubkey);
      if (idx === undefined) {
        throw new Error(`Account ${acc.pubkey} not found in account keys`);
      }
      accountIndexes[i] = idx;
    }
    parts.push(accountIndexes);

    // Instruction data
    let dataBytes: Uint8Array;
    if (ix.data.startsWith('0x')) {
      dataBytes = hexToBytes(ix.data);
    } else {
      // Assume base58
      dataBytes = base58Decode(ix.data);
    }
    parts.push(encodeCompactU16(dataBytes.length));
    parts.push(dataBytes);
  }

  return concatBytes(parts);
}

/**
 * Encode a number as a compact-u16 (Solana's variable-length integer format).
 * Similar to Protocol Buffers VLQ encoding.
 */
function encodeCompactU16(value: number): Uint8Array {
  if (value < 0 || value > 0xffff) {
    throw new Error(`compact-u16 value out of range: ${value}`);
  }

  if (value < 0x80) {
    return new Uint8Array([value]);
  }

  if (value < 0x4000) {
    return new Uint8Array([
      (value & 0x7f) | 0x80,
      value >> 7,
    ]);
  }

  return new Uint8Array([
    (value & 0x7f) | 0x80,
    ((value >> 7) & 0x7f) | 0x80,
    value >> 14,
  ]);
}

function concatBytes(arrays: Uint8Array[]): Uint8Array {
  let totalLength = 0;
  for (const arr of arrays) {
    totalLength += arr.length;
  }
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const arr of arrays) {
    result.set(arr, offset);
    offset += arr.length;
  }
  return result;
}
